import { useNavigate } from "react-router-dom";
import { Sparkles, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";

const About = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto pt-16">
        <div className="fixed top-4 left-4 z-50">
          <BackButton onClick={() => navigate(-1)} />
        </div>

        {/* About Me Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold mb-6">{t('about.title')}</h1>
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
            <img
              src="/aar.png"
              alt={t('about.team_name')}
              className="w-32 h-32 rounded-full object-cover flex-shrink-0"
            />
            <div className="flex-1">
              <p className="text-lg mb-3">
                {t('about.team_desc_1')}
                <a
                  href="https://aaronfrost.net"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-primary hover:underline"
                >{t('about.team_name')}</a>{t('about.team_desc_2')}
              </p>
              <p className="text-foreground/80">
                {t('about.intro')}
              </p>
            </div>
          </div>
        </motion.section>

        {/* Personal Note - Moved to top */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="bg-card border border-border rounded-xl p-6 mb-10"
        >
          <p className="text-lg mb-4">{t('about.personal_note_1')}</p>
          <p className="mb-4">
            <Link
              to="/iamguy"
              className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
            >
              {t('about.check_profile')}
            </Link>
          </p>
          <p className="text-foreground/80 mb-6">{t('about.personal_note_2')}</p>
          
          {/* Indie Developer Support Bubble */}
          <Card className="p-4 border border-pink-500/30 bg-gradient-to-r from-pink-500/5 to-purple-500/5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-500/10 rounded-full flex-shrink-0">
                <Heart className="h-5 w-5 text-pink-500 fill-pink-500" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">{t('upgrade.indie_support.title')}</h4>
                <Link 
                  to="/upgrade" 
                  className="inline-flex items-center text-xs font-medium text-pink-500 hover:text-pink-600 hover:underline"
                >
                  {t('about.try_pro_support')} →
                </Link>
              </div>
            </div>
          </Card>
        </motion.section>

        <div className="space-y-10 text-foreground/90">
          {/* Why I Built This */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h2 className="text-2xl font-semibold mb-4">{t('about.why_built_title')}</h2>
            
            <h3 className="text-lg font-medium mb-3 text-green-500">{t('about.i_need')}</h3>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-6 text-foreground/80">
              <li>{t('about.need_1')}</li>
              <li>{t('about.need_2')}</li>
              <li>{t('about.need_3')}</li>
              <li>{t('about.need_4')}</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 text-red-500">{t('about.i_hate')}</h3>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-2 text-foreground/80">
              <li>{t('about.hate_1')}</li>
              <li>
                <span className="font-medium">{t('about.hate_2_title')}</span>
                <ul className="list-disc list-inside space-y-1 ml-6 mt-1 text-sm text-muted-foreground">
                  <li>{t('about.hate_2_1')}</li>
                  <li>{t('about.hate_2_2')}</li>
                  <li>{t('about.hate_2_3')}</li>
                  <li>{t('about.hate_2_4')}</li>
                  <li>{t('about.hate_2_5')}</li>
                </ul>
              </li>
            </ul>
          </motion.section>

          {/* Animated Contribution Chart Visual Break */}
          <ContributionChartAnimation />

          {/* With GitFit */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-2xl font-semibold mb-4">{t('about.with_gitfit_title')}</h2>
            
            <h3 className="text-lg font-medium mb-3">{t('about.track_anything')}</h3>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-6 text-foreground/80">
              <li>{t('about.track_1')}</li>
              <li>{t('about.track_2')}</li>
              <li>{t('about.track_3')}</li>
              <li>{t('about.track_4')}</li>
              <li>{t('about.track_5')}</li>
              <li>{t('about.track_6')}</li>
              <li>{t('about.track_7')}</li>
            </ul>

            <h3 className="text-lg font-medium mb-3">{t('about.see_your_way')}</h3>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-6 text-foreground/80">
              <li>{t('about.way_1')}</li>
              <li>{t('about.way_2')}</li>
              <li>{t('about.way_3')}</li>
              <li>{t('about.way_4')}</li>
              <li>{t('about.way_5')}</li>
            </ul>
          </motion.section>

          {/* Animated Metrics Visual Break */}
          <ScrollingMetricsAnimation />

          {/* Food Scanner */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h2 className="text-2xl font-semibold mb-4">{t('about.food_scanner_title')}</h2>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-6 text-foreground/80">
              <li>{t('about.scanner_1')}</li>
              <li>{t('about.scanner_2')}</li>
              <li>{t('about.scanner_3')}</li>
              <li>{t('about.scanner_4')}</li>
              <li>{t('about.scanner_5')}</li>
              <li>{t('about.scanner_6')}</li>
            </ul>
          </motion.section>

          {/* GitHub Charts */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            <h2 className="text-2xl font-semibold mb-4">{t('about.charts_title')}</h2>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-6 text-foreground/80">
              <li>{t('about.charts_1')}</li>
              <li>{t('about.charts_2')}</li>
              <li>{t('about.charts_3')}</li>
              <li>{t('about.charts_4')}</li>
            </ul>
          </motion.section>

          {/* Custom Metrics Animation Visual Break */}
          <CustomMetricsAnimation />

          {/* More Cool Stuff */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
          >
            <h2 className="text-2xl font-semibold mb-4">{t('about.more_cool_title')}</h2>
            <ul className="space-y-2 ml-4 mb-6 text-foreground/80">
              <li>{t('about.cool_1')}</li>
              <li>{t('about.cool_2')}</li>
              <li>{t('about.cool_3')}</li>
              <li>{t('about.cool_4')}</li>
              <li>{t('about.cool_5')}</li>
            </ul>
          </motion.section>

          {/* Links */}
          <motion.section
            className="border-t border-border pt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <h2 className="text-2xl font-semibold mb-4">{t('about.more_info')}</h2>
            <div className="flex flex-col gap-2">
              <Button
                variant="link"
                className="justify-start p-0 h-auto"
                onClick={() => navigate("/terms-and-conditions")}
              >
                {t('legal.terms.title')}
              </Button>
              <Button
                variant="link"
                className="justify-start p-0 h-auto"
                onClick={() => navigate("/privacy-policy")}
              >
                {t('legal.privacy.title')}
              </Button>
              <Button
                variant="link"
                className="justify-start p-0 h-auto"
                onClick={() => navigate("/contact-us")}
              >
                {t('nav.contact_us')}
              </Button>
            </div>
          </motion.section>

          {/* Disclaimer */}
          <section className="text-sm text-muted-foreground border-t border-border pt-8">
            <p>
              {t('about.disclaimer')}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Animated Contribution Chart Component
const ContributionChartAnimation = () => {
  const { t } = useTranslation();
  const weeks = Array.from({ length: 12 }, (_, i) => i);
  const days = Array.from({ length: 7 }, (_, i) => i);

  const getIntensity = (week: number, day: number) => {
    const value = (week * 7 + day + Math.random() * 30) % 110;
    if (value >= 100) return 4;
    if (value >= 75) return 3;
    if (value >= 50) return 2;
    if (value >= 25) return 1;
    return 0;
  };

  const getColor = (intensity: number) => {
    const colors = [
      "bg-[hsl(var(--contrib-0))]",
      "bg-[hsl(var(--contrib-1))]",
      "bg-[hsl(var(--contrib-2))]",
      "bg-[hsl(var(--contrib-3))]",
      "bg-[hsl(var(--contrib-4))]",
    ];
    return colors[intensity];
  };

  return (
    <div className="my-12 space-y-6 flex flex-col items-center justify-center py-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="flex gap-1 justify-center p-6 bg-card rounded-xl shadow-lg border border-border max-w-full overflow-hidden"
      >
        {weeks.map((week) => (
          <div key={week} className="flex flex-col gap-1">
            {days.map((day) => {
              const intensity = getIntensity(week, day);
              return (
                <motion.div
                  key={day}
                  initial={{ scale: 0, rotate: -90, opacity: 0 }}
                  whileInView={{ scale: 1, rotate: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    delay: (week * 7 + day) * 0.003,
                    duration: 0.3,
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                  }}
                  className={`w-3 h-3 rounded-sm ${getColor(intensity)}`}
                />
              );
            })}
          </div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
        className="flex items-center justify-center gap-2 text-xs"
      >
        <span className="text-muted-foreground font-medium">{t('about.chart_less')}</span>
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{
              delay: 0.6 + i * 0.05,
              type: "spring",
              stiffness: 350,
            }}
            className={`w-3 h-3 rounded-sm ${getColor(i)}`}
          />
        ))}
        <span className="text-muted-foreground font-medium">{t('about.chart_more')}</span>
      </motion.div>
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.6 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8 }}
        className="text-sm text-muted-foreground text-center"
      >
        {t('about.chart_caption')}
      </motion.p>
    </div>
  );
};

// Scrolling Metrics Animation Component
const ScrollingMetricsAnimation = () => {
  const { t } = useTranslation();
  const metrics = [
    { emoji: "🔥", progress: 85 },
    { emoji: "🍗", progress: 60 },
    { emoji: "🏃", progress: 45 },
    { emoji: "💪", progress: 92 },
    { emoji: "😴", progress: 75 },
    { emoji: "💧", progress: 40 },
  ];

  const getProgressColor = (progress: number) => {
    if (progress >= 100) return "rgb(34, 197, 94)";
    if (progress >= 75) return "rgb(132, 204, 22)";
    if (progress >= 50) return "rgb(234, 179, 8)";
    if (progress >= 25) return "rgb(249, 115, 22)";
    return "rgb(239, 68, 68)";
  };

  return (
    <div className="my-12 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center justify-center space-y-6"
      >
        <div className="relative overflow-hidden h-20 w-full">
          <motion.div
            animate={{
              x: [0, -150],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "linear",
            }}
            className="flex gap-4 absolute"
          >
            {[...metrics, ...metrics, ...metrics, ...metrics].map((metric, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  delay: idx * 0.02,
                  type: "spring",
                  stiffness: 200,
                }}
                className="flex-shrink-0 relative"
              >
                <div
                  className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-2xl relative overflow-hidden border border-accent/10"
                >
                  <svg className="absolute inset-0 w-full h-full -rotate-90">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      fill="none"
                      stroke={getProgressColor(metric.progress)}
                      strokeWidth="4"
                      style={{
                        strokeDasharray: `${2 * Math.PI * 28}`,
                        strokeDashoffset: `${2 * Math.PI * 28 * (1 - metric.progress / 100)}`,
                      }}
                      opacity="0.9"
                    />
                  </svg>
                  <span className="relative z-10">{metric.emoji}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />
          <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.6 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-sm text-muted-foreground text-center"
        >
          {t('about.metrics_caption')}
        </motion.p>
      </motion.div>
    </div>
  );
};

// Custom Metrics Animation Component
const CustomMetricsAnimation = () => {
  const { t } = useTranslation();
  const metrics = [
    { name: t('about.demo_piano'), value: "45 min", icon: "🎹", color: "from-purple-50 to-purple-100/50", border: "border-purple-300/60", text: "text-purple-900" },
    { name: t('about.demo_rating'), value: "8.5", icon: "✨", subtitle: t('about.demo_with_ai'), color: "from-pink-50 to-pink-100/50", border: "border-pink-300/60", text: "text-pink-900" },
    { name: t('about.demo_commits'), value: "12", icon: "💻", color: "from-slate-50 to-slate-100/50", border: "border-slate-300/60", text: "text-slate-900" },
    { name: t('about.demo_calories'), value: "2,240", icon: "🔥", color: "from-orange-50 to-orange-100/50", border: "border-orange-300/60", text: "text-orange-900" },
    { name: t('about.demo_fiber'), value: "28g", icon: "🥗", color: "from-green-50 to-green-100/50", border: "border-green-300/60", text: "text-green-900" },
    { name: t('about.demo_protein'), value: "156g", icon: "💪", color: "from-blue-50 to-blue-100/50", border: "border-blue-300/60", text: "text-blue-900" },
  ];

  return (
    <div className="my-12 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center justify-center space-y-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-purple-500" />
          <h3 className="text-xl font-semibold">{t('about.custom_metrics_title')}</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-4xl">
          {metrics.map((metric, idx) => (
            <motion.div
              key={metric.name}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.3,
                delay: idx * 0.1,
              }}
              className={`bg-gradient-to-br ${metric.color} border-2 ${metric.border} rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow`}
            >
              <div className="flex flex-col items-center gap-2">
                <span className="text-4xl">{metric.icon}</span>
                <div className="text-center">
                  <div className={`font-bold ${metric.text} text-sm flex items-center justify-center gap-1`}>
                    {metric.name}
                    {metric.subtitle && (
                      <span className="text-xs font-normal opacity-70">({metric.subtitle})</span>
                    )}
                  </div>
                  <div className={`${metric.text} text-2xl font-bold mt-1`}>
                    {metric.value}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.6 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-sm text-muted-foreground text-center"
        >
          {t('about.custom_metrics_caption')}
        </motion.p>
      </motion.div>
    </div>
  );
};

export default About;
